import { useCallback, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useIdentity } from '../../identity/state/useIdentity';
import { Button } from '../../../shared/components/Button';
import { AccessGate } from '../components/AccessGate';
import { ErrorSummary, StepHeading } from '../components/basics';
import { PhiConfirmDialog } from '../components/ConfirmDialog';
import { ConfirmStep } from '../components/ConfirmStep';
import { EmployeesStep } from '../components/EmployeesStep';
import { FoundStep } from '../components/FoundStep';
import { MapColumnsStep } from '../components/MapColumnsStep';
import { PreviewStep } from '../components/PreviewStep';
import { ProblemsStep } from '../components/ProblemsStep';
import { ProviderStep } from '../components/ProviderStep';
import { ResultsStep } from '../components/ResultsStep';
import { UploadStep } from '../components/UploadStep';
import { WizardFrame } from '../components/WizardFrame';
import { describeError, isNotFound } from '../describeError';
import { CANCELABLE_STATUSES, STATUS_META } from '../format';
import { useAsyncData } from '../hooks/useAsyncData';
import { useIsMounted } from '../hooks/useIsMounted';
import { payrollHistoryImportGateway as gateway } from '../index';
import { readDraftBatch, writeDraftBatch } from '../draftBatch';
import { NOT_NEEDED_NOTE, STEP_INDEX } from '../wizardSteps';
import {
  IMPORT_BASE, batchStepPath, isBatchStep, legacyStep, stepForBatch, usesGuidedMapping, type BatchStep,
} from '../routes';
import type { BatchDetail, BatchStatus, Capabilities, ProviderInfo } from '../types';
import '../payroll-history-import.css';

const IN_FLIGHT_STATUSES: BatchStatus[] = ['Uploaded', 'Validating', 'Importing'];
const UPLOADED_STATUSES: BatchStatus[] = ['ColumnMappingRequired', 'ValidationFailed', 'ReadyForReview'];

/** Step 1 (no batch exists yet): /new, with the chosen provider optionally in the address. */
function NewImportFlow({ capabilities }: { capabilities: Capabilities }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [announcement, setAnnouncement] = useState('');

  const start = async (provider: ProviderInfo): Promise<string | null> => {
    try {
      // Back -> Continue within the same wizard session reuses the Draft already created instead of orphaning it.
      const draft = readDraftBatch();
      if (draft && draft.sourceSystem === provider.sourceSystem && (draft.provider ?? provider.id) === provider.id) {
        try {
          const existing = await gateway.getBatch(draft.id);
          if (existing.status === 'Created' && existing.sourceSystem === provider.sourceSystem) {
            navigate(batchStepPath(existing.id, 'upload'));
            return null;
          }
        } catch {
          /* stale or not ours any more: start a fresh one below */
        }
      }
      const batch = await gateway.startImport(provider.sourceSystem, provider.id);
      writeDraftBatch({ id: batch.id, sourceSystem: provider.sourceSystem, provider: provider.id });
      navigate(batchStepPath(batch.id, 'upload'));
      return null;
    } catch (err) {
      return describeError(err, 'Could not start the import.');
    }
  };

  return (
    <WizardFrame currentIndex={STEP_INDEX.source} announcement={announcement}>
      <ProviderStep capabilities={capabilities} announce={setAnnouncement} initialProviderId={searchParams.get('provider')} onStart={start} />
    </WizardFrame>
  );
}

function StepUnavailable({ batch, children }: { batch: BatchDetail; children: ReactNode }) {
  const target = stepForBatch(batch);
  return (
    <div className="phi-notice">
      <p>{children}</p>
      <Link to={batchStepPath(batch.id, target)}>Go to the current step</Link>
    </div>
  );
}

/** Step 4 for a native EPN format: nothing to match, and the way on. */
function ColumnsNotNeeded({ batch }: { batch: BatchDetail }) {
  const ready = UPLOADED_STATUSES.includes(batch.status);
  return (
    <div className="phi-notice">
      <p><strong>{NOT_NEEDED_NOTE}.</strong></p>
      <p className="muted">Column matching is only needed for a spreadsheet or CSV from another system. Go on to match your employees.</p>
      <div className="phi-footer">
        <Link to={batchStepPath(batch.id, 'found')} className="phi-btn-secondary">Back</Link>
        {ready ? (
          <Link to={batchStepPath(batch.id, 'employees')} className="phi-btn-primary">Next: Match your employees</Link>
        ) : (
          <Link to={batchStepPath(batch.id, stepForBatch(batch))}>Go to the current step</Link>
        )}
      </div>
    </div>
  );
}

/** Steps 2-9, driven by /:batchId/:step so refresh and the back button land on the right step. */
function BatchFlow({ batchId, step, capabilities }: { batchId: string; step: BatchStep; capabilities: Capabilities }) {
  const navigate = useNavigate();
  const { me } = useIdentity();
  const companyName = me?.memberships.find((m) => m.companyId === me.activeCompanyId)?.companyName ?? '—';
  const load = useCallback((signal: AbortSignal) => gateway.getBatch(batchId, signal), [batchId]);
  const { loading, data: batch, error, reload, setData } = useAsyncData(load);
  const [announcement, setAnnouncement] = useState('');
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const mounted = useIsMounted();

  const refreshBatch = useCallback(async () => {
    setData(await gateway.getBatch(batchId));
  }, [batchId, setData]);

  const cancelImport = async () => {
    setCanceling(true);
    setActionError(null);
    try {
      const result = await gateway.cancel(batchId);
      if (!mounted.current) return;
      setData(result);
      setConfirmingCancel(false);
      setAnnouncement('The import was canceled.');
      navigate(batchStepPath(batchId, 'results'));
    } catch (err) {
      if (!mounted.current) return;
      setConfirmingCancel(false);
      setActionError(describeError(err, 'Could not cancel this import.'));
    } finally {
      if (mounted.current) setCanceling(false);
    }
  };

  // Uploaded / Validating / Importing are in-flight statuses the server moves on by itself; give the user a way to
  // re-check rather than leaving a dead end.
  const refreshStatus = async () => {
    setRefreshing(true);
    setActionError(null);
    try {
      await refreshBatch();
      if (mounted.current) setAnnouncement('Status refreshed.');
    } catch (err) {
      if (mounted.current) setActionError(describeError(err, 'Could not refresh the status.'));
    } finally {
      if (mounted.current) setRefreshing(false);
    }
  };

  const currentIndex = STEP_INDEX[step];

  if (loading) {
    return (
      <WizardFrame key={step} currentIndex={currentIndex} announcement="">
        <p className="muted" role="status">Loading import…</p>
      </WizardFrame>
    );
  }

  if (error !== undefined || !batch) {
    // A missing batch and another company's batch are indistinguishable (404), and show no data either way.
    return (
      <div className="phi-state">
        <StepHeading>{isNotFound(error) ? 'Import not found' : 'Import Payroll Data'}</StepHeading>
        {isNotFound(error) ? (
          <>
            <p>We couldn&apos;t find that import. It may not exist, or you may not have access to it.</p>
            <Link to={IMPORT_BASE}>Back to import history</Link>
          </>
        ) : (
          <>
            <ErrorSummary message={describeError(error, 'Could not load this import.')} />
            <button type="button" className="phi-btn-secondary" onClick={reload}>Try again</button>
          </>
        )}
      </div>
    );
  }

  const guided = usesGuidedMapping(batch);
  const validated = batch.status === 'ValidationFailed' || batch.status === 'ReadyForReview';

  let body: ReactNode;
  switch (step) {
    case 'upload':
      body = <UploadStep batch={batch} capabilities={capabilities} announce={setAnnouncement} onBatchChange={setData} />;
      break;
    case 'found':
      body = batch.status === 'Created'
        ? <StepUnavailable batch={batch}>There is nothing to check yet. Upload your file first.</StepUnavailable>
        : <FoundStep batch={batch} capabilities={capabilities} />;
      break;
    case 'map':
      body = !guided
        ? <ColumnsNotNeeded batch={batch} />
        : UPLOADED_STATUSES.includes(batch.status)
          ? <MapColumnsStep batch={batch} announce={setAnnouncement} refreshBatch={refreshBatch} />
          : <StepUnavailable batch={batch}>Columns can only be matched after a file is uploaded and before the import is finished or canceled.</StepUnavailable>;
      break;
    case 'employees':
      body = validated
        ? <EmployeesStep batch={batch} capabilities={capabilities} announce={setAnnouncement} refreshBatch={refreshBatch} />
        : batch.status === 'ColumnMappingRequired'
          ? <StepUnavailable batch={batch}>Match your columns first. Once EPN can read your file, you can match its employees.</StepUnavailable>
          : <StepUnavailable batch={batch}>Validation results are only available before an import is finished or canceled, and after a file is uploaded.</StepUnavailable>;
      break;
    case 'totals':
      body = batch.status === 'ReadyForReview'
        ? <PreviewStep batch={batch} />
        : <StepUnavailable batch={batch}>The preview is available once validation has passed and every employee is matched.</StepUnavailable>;
      break;
    case 'problems':
      body = validated
        ? <ProblemsStep batch={batch} capabilities={capabilities} companyName={companyName} announce={setAnnouncement} refreshBatch={refreshBatch} />
        : <StepUnavailable batch={batch}>Problems are listed once your file has been checked, and before the import is finished or canceled.</StepUnavailable>;
      break;
    case 'confirm':
      body = batch.status === 'ReadyForReview'
        ? <ConfirmStep batch={batch} capabilities={capabilities} announce={setAnnouncement} onBatchChange={setData} />
        : <StepUnavailable batch={batch}>This import is not ready to be confirmed.</StepUnavailable>;
      break;
    case 'results':
      body = <ResultsStep batch={batch} capabilities={capabilities} announce={setAnnouncement} />;
      break;
  }

  // Mirrors the server: cancel your own import, or any import with the admin permission. (Display only.)
  const ownsBatch = !me || batch.createdByUserId === me.userId;
  const canCancel =
    capabilities.canCreate && (ownsBatch || capabilities.canAdmin) && step !== 'results' && CANCELABLE_STATUSES.includes(batch.status);
  const inFlight = IN_FLIGHT_STATUSES.includes(batch.status);

  return (
    <WizardFrame key={step} currentIndex={currentIndex} announcement={announcement} notNeeded={guided ? [] : [STEP_INDEX.map]}>
      <p className="muted">Import status: {STATUS_META[batch.status].label}</p>
      {inFlight && (
        <p>
          <Button type="button" className="phi-btn-secondary" onClick={() => void refreshStatus()} loading={refreshing} loadingLabel="Refreshing…">
            Refresh status
          </Button>
        </p>
      )}
      {actionError && <ErrorSummary message={actionError} />}
      {body}
      {canCancel && (
        <div className="phi-actions">
          <button type="button" className="phi-btn-secondary" onClick={() => setConfirmingCancel(true)}>
            Cancel import
          </button>
        </div>
      )}
      {confirmingCancel && (
        <PhiConfirmDialog
          title="Cancel this import?"
          body={<p>Nothing from this file will be imported. You can start a new import at any time.</p>}
          confirmLabel="Cancel import"
          cancelLabel="Keep import"
          busy={canceling}
          onConfirm={() => void cancelImport()}
          onCancel={() => setConfirmingCancel(false)}
        />
      )}
    </WizardFrame>
  );
}

export function ImportWizardPage() {
  const { batchId, step } = useParams();
  const isNew = batchId === undefined;
  const validStep = isBatchStep(step) ? step : null;
  // The results step only needs `read` (it just reports an outcome); every other step needs `create`.
  const requirement = !isNew && validStep === 'results' ? 'read' : 'create';
  const replacement = legacyStep(step);

  if (!isNew && replacement) return <Navigate to={batchStepPath(batchId, replacement)} replace />;

  return (
    <section className="card card--wide phi">
      <AccessGate require={requirement}>
        {(capabilities) => {
          if (isNew) return <NewImportFlow capabilities={capabilities} />;
          if (!validStep) {
            return (
              <div className="phi-state">
                <StepHeading>Import not found</StepHeading>
                <p>We couldn&apos;t find that step.</p>
                <Link to={IMPORT_BASE}>Back to import history</Link>
              </div>
            );
          }
          return <BatchFlow batchId={batchId} step={validStep} capabilities={capabilities} />;
        }}
      </AccessGate>
    </section>
  );
}
